import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { allProducts } from "./shop";
import "./shop.css";
import { FaHeart, FaRegHeart, FaArrowLeft, FaShare } from "react-icons/fa";
import ProductReviews from "../components/ProductReviews";
import RecentlyViewed from "../components/RecentlyViewed";
import ProductRecommendations from "../components/ProductRecommendations";
import OfferDisplay from "../components/OfferDisplay.js";
import { setPageMeta } from "../utils/seo";

const ProductDetail = ({
  addToCart,
  wishlist,
  addToWishlist,
  removeFromWishlist,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = allProducts.find((p) => String(p.id) === String(id));
  const isWishlisted = wishlist && wishlist.some((item) => item.id === product?.id);
  const [imgZoom, setImgZoom] = useState(false);

  useEffect(() => {
    if (!product) {
      return;
    }

    setPageMeta({
      title: `${product.name} | Banat India`,
      description:
        "View product details, features, and pricing for Banat India oral care items.",
      keywords:
        "toothbrush details, oral care product, Banat India product, dental hygiene",
    });
  }, [product]);

  const handleShare = async () => {
    const shareData = {
      title: product.name,
      text: `Check out this amazing product: ${product.name} - ₹${product.price}`,
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback for browsers that don't support Web Share API
        await navigator.clipboard.writeText(shareData.url);
        alert('Product link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(shareData.url);
        alert('Product link copied to clipboard!');
      } catch (clipboardError) {
        console.error('Error copying to clipboard:', clipboardError);
        alert('Unable to share product');
      }
    }
  };

  if (!product) return <div className="product-not-found">Product not found</div>;

  return (
    <div className="pd-creative-page">
      <div className="pd-back">
        <Link to="/shop"><FaArrowLeft /> Back to Collection</Link>
      </div>
      
      <div className="pd-hero">
        <div className="pd-image-col">
          <div
            className={`pd-image-stage${imgZoom ? " zoomed" : ""}`}
            onMouseEnter={() => setImgZoom(true)}
            onMouseLeave={() => setImgZoom(false)}
          >
            <div className="pd-blob"></div>
            <img
              src={product.image}
              alt={product.name}
              className="pd-image"
            />
          </div>
        </div>
        
        <div className="pd-info-col">
          <div className="pd-tags">
            <span className="pd-tag">{product.category}</span>
            <span className="pd-tag outline">{product.type}</span>
          </div>
          
          <div className="pd-header">
            <h1 className="pd-title">{product.name}</h1>
            <button
              className="pd-wishlist-btn"
              onClick={() =>
                isWishlisted ? removeFromWishlist(product) : addToWishlist(product)
              }
              aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
            >
              {isWishlisted ? <FaHeart color="#ff2d55" /> : <FaRegHeart />}
            </button>
          </div>
          
          <div className="pd-price-row">
            <span className="pd-price">₹{product.price}</span>
            <div className="pd-offer-container">
              <OfferDisplay productId={product.id} />
            </div>
          </div>
          
          <div className="pd-description">
            <p>{product.explanation}</p>
          </div>
          
          {product.features && Array.isArray(product.features) && (
            <div className="pd-features">
              <h3>Why you'll love it</h3>
              <ul>
                {product.features.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="pd-specs">
            <div className="pd-spec-box">
              <span className="pd-spec-label">Category</span>
              <span className="pd-spec-value">{product.category}</span>
            </div>
            <div className="pd-spec-box">
              <span className="pd-spec-label">Type</span>
              <span className="pd-spec-value">{product.type}</span>
            </div>
            {product.id && (
              <div className="pd-spec-box">
                <span className="pd-spec-label">SKU</span>
                <span className="pd-spec-value">#{product.id}</span>
              </div>
            )}
          </div>
          
          <div className="pd-actions">
            <button
              className="pd-btn primary"
              onClick={() => addToCart(product)}
            >
              Add to cart
            </button>
            <button
              className="pd-btn secondary"
              onClick={() => {
                addToCart(product);
                navigate("/cart");
              }}
            >
              Buy Now
            </button>
            <button
              className="pd-btn icon"
              onClick={handleShare}
              aria-label="Share product"
              title="Share this product"
            >
              <FaShare />
            </button>
          </div>
        </div>
      </div>

      <div className="pd-bottom-sections">
        <div className="pd-reviews">
          <h2 className="pd-section-title">Customer Reviews</h2>
          <ProductReviews 
            productId={product.id} 
            productName={product.name}
          />
        </div>

        <div className="pd-related">
          <div className="pd-related-box">
            <RecentlyViewed currentProduct={product} maxItems={4} />
          </div>
          <div className="pd-related-box">
            <ProductRecommendations currentProduct={product} maxItems={6} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
